import { useCallback, useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import {
  BuyXPayY,
  PaymentOptionInfoDto,
  ProgressiveDiscount,
  Reward,
} from '@tiendanube/common';
import { useNavegate } from 'App/hooks';
import { mergeStatusType, toStatusType } from 'App/rtk';
import { ErrorScreen } from 'commons/components';
import { useForm, useToastStatus } from 'commons/hooks';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { promotionDefault } from './constants';
import { ScopeValuesInterface } from './types';
import { validationSchema } from './validationSchema';
import { paymentOptionHasLegacyDiscount } from '../../components/PaymentOptions/utils';
import {
  PromotionPage,
  PromotionsDetailsSkeletons,
  PromotionForm,
} from '../components';
import { useGetPaymentOptionsForDiscountConfiguration } from '../hooks';
import {
  useGetPromotionQuery,
  usePromotionTypesQuery,
  useUpdatePromotionMutation,
} from '../promotionsApi';

export function EditPromotionPage() {
  const t = useTranslationMarketing('promotions.settingsPage.edit');

  const { id } = useParams<{ id: string }>();
  const {
    data,
    status: statusLoadPromotion,
    refetch: refetchPromotions,
  } = useGetPromotionQuery(id);
  const { goBack } = useNavegate();

  const [isExternal, setIsExternal] = useState(false);

  const [updatePromotion, { status: statusUpdate }] =
    useUpdatePromotionMutation();

  const { status: statusLoadType, refetch: refetchTypes } =
    usePromotionTypesQuery();

  const { paymentOptions: activePaymentOptions } =
    useGetPaymentOptionsForDiscountConfiguration();

  const initialValues = useMemo(
    () => ({
      ...(data ?? promotionDefault),
      customLabelEnabled: !!(data?.customLabel ?? promotionDefault.customLabel),
    }),
    [data],
  );

  const {
    values,
    errors,
    isDirty,
    handleChange,
    setFieldValue,
    handleChangeSelect,
    handleOnSubmit,
  } = useForm({
    initialValues,
    validationSchema,
    onSubmit: (data) => {
      const { customLabelEnabled, ...rest } = data;
      const transformedData = {
        ...rest,
        customLabel: customLabelEnabled ? rest.customLabel : null,
      };
      updatePromotion({
        id,
        payload: transformedData,
      });
    },
  });

  useEffect(() => {
    if (
      data?.paymentOptions &&
      data.paymentOptions.length > 0 &&
      activePaymentOptions.length > 0
    ) {
      const filteredPaymentOptions = data.paymentOptions.filter(
        (selectedOption) =>
          !paymentOptionHasLegacyDiscount(selectedOption, activePaymentOptions),
      );

      if (filteredPaymentOptions.length !== data.paymentOptions.length) {
        setFieldValue('paymentOptions', filteredPaymentOptions);
      }
    }
  }, [data?.paymentOptions, activePaymentOptions, setFieldValue]);

  const handleChangeCategories = (scopeValues: ScopeValuesInterface[]) => {
    setFieldValue('categories', scopeValues);
  };

  const handleChangeProducts = (scopeValues: ScopeValuesInterface[]) => {
    setFieldValue('products', scopeValues);
  };

  const handleChangeProgressiveDiscounts = (
    progressiveDiscounts: ProgressiveDiscount[],
  ) => {
    setFieldValue('progressiveDiscounts', progressiveDiscounts);
  };

  const handleChangeBuyXPayY = (buyXPayY: BuyXPayY | null) => {
    setFieldValue('buyXPayY', buyXPayY);
  };

  const handleChangeRewards = (rewards: Reward[]) => {
    setFieldValue('rewards', rewards);
  };

  const handleChangePercentageOff = (percentageOff: number) => {
    setFieldValue('percentageOffDiscount', percentageOff);
  };

  const handleChangePaymentOptions = (
    paymentOptions: PaymentOptionInfoDto[],
  ) => {
    setFieldValue('paymentOptions', paymentOptions);
  };

  const handleChangeIsExternal = useCallback((value: boolean) => {
    setIsExternal(value);
  }, []);

  const saveStatus = toStatusType(statusUpdate);
  const loadPomotionStatus = toStatusType(statusLoadPromotion);
  const loadTypeStatus = toStatusType(statusLoadType);
  const loadStatus = mergeStatusType([loadPomotionStatus, loadTypeStatus]);

  const refetch = () => {
    if (loadPomotionStatus === 'error') refetchPromotions();
    if (loadTypeStatus === 'error') refetchTypes();
  };

  useToastStatus({
    error: t('error'),
    success: t('success'),
    status: saveStatus,
    onSuccess: goBack,
  });

  return (
    <PromotionPage
      mode="edit"
      isDirty={isDirty}
      isExternal={isExternal}
      saveStatus={saveStatus}
      loadStatus={loadStatus}
      onSave={handleOnSubmit}
    >
      {loadStatus === 'error' && <ErrorScreen onRetry={refetch} />}
      {loadStatus === 'loading' && <PromotionsDetailsSkeletons />}
      {loadStatus === 'success' && (
        <PromotionForm
          mode="edit"
          values={values}
          errors={errors}
          isExternal={isExternal}
          promotionId={id}
          onChange={handleChange}
          setFieldValue={setFieldValue}
          onChangeSelect={handleChangeSelect}
          onChangeCategories={handleChangeCategories}
          onChangeProducts={handleChangeProducts}
          onChangeIsExternal={handleChangeIsExternal}
          onChangeProgressiveDiscounts={handleChangeProgressiveDiscounts}
          onChangeBuyXPayY={handleChangeBuyXPayY}
          onChangeRewards={handleChangeRewards}
          onChangePercentageOff={handleChangePercentageOff}
          onChangePaymentOptions={handleChangePaymentOptions}
        />
      )}
    </PromotionPage>
  );
}
